import { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/lib/statusUtils";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, ShieldCheck, Trash2, Pencil, Download, UserPlus,
  Ban, RotateCcw, History, AlertTriangle,
} from "lucide-react";

import { roleLabel } from "@/lib/statusUtils";

const emptyCreate = { name: "", email: "", mobile: "", password: "", role: "agent", company_name: "" };
const emptyEdit = { name: "", mobile: "", role: "", company_name: "", status: "", password: "" };

// Normalize legacy 'approved' → 'active' and 'pending' → 'pending_approval' for display
const normalizeStatus = (s) => {
  if (s === "approved") return "active";
  if (s === "pending") return "pending_approval";
  return s || "active";
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending_approval");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);

  // Suspend / reactivate / history state
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendForm, setSuspendForm] = useState({ reason: "", notes: "" });
  const [suspendWarnings, setSuspendWarnings] = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [reactivateNotes, setReactivateNotes] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);

  const isSuper = user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || isSuper;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", { params: tab !== "all" ? { status_filter: tab } : {} });
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (id, role = "agent") => {
    try { await api.post(`/users/${id}/approve`, { role }); toast.success(`Approved as ${role}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/users/${id}/reject`); toast.success("Rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", createForm);
      toast.success("User created");
      setCreateForm(emptyCreate); setCreateOpen(false); load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", mobile: u.mobile || "", role: u.role || "agent", company_name: u.company_name || "", status: normalizeStatus(u.status), password: "" });
  };
  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("User updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Suspension flow ----------
  const canSuspend = (target) => {
    if (!target || target.id === user?.id) return false;
    const role = target.role;
    if (isSuper) return role !== "super_admin";
    if (user?.role === "admin") return ["agent", "buyer", "staff"].includes(role);
    return false;
  };

  const openSuspend = async (u) => {
    setSuspendTarget(u);
    setSuspendForm({ reason: "", notes: "" });
    setSuspendWarnings(null);
    try {
      const { data } = await api.get(`/users/${u.id}/warnings`);
      setSuspendWarnings(data);
    } catch { setSuspendWarnings({ active_bookings: 0 }); }
  };
  const confirmSuspend = async () => {
    if (!suspendForm.reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/users/${suspendTarget.id}/suspend`, suspendForm);
      toast.success(`${suspendTarget.name} suspended`);
      setSuspendTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openReactivate = (u) => { setReactivateTarget(u); setReactivateNotes(""); };
  const confirmReactivate = async () => {
    try {
      await api.post(`/users/${reactivateTarget.id}/reactivate`, { notes: reactivateNotes });
      toast.success(`${reactivateTarget.name} reactivated`);
      setReactivateTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openHistory = async (u) => {
    setHistoryTarget(u);
    setHistoryEntries([]);
    try {
      const { data } = await api.get(`/users/${u.id}/history`);
      setHistoryEntries(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadExport = () => {
    const token = localStorage.getItem("anu_token");
    fetch(`${API_BASE}/export/users.xlsx`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "anuttara-users.xlsx"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Admin</div>
            <h1 className="font-heading text-2xl md:text-4xl">Users & Approvals</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setCreateOpen(true)} data-testid="create-user-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">
              <UserPlus className="h-4 w-4 mr-2" /> Add user
            </Button>
            <Button onClick={downloadExport} data-testid="export-users-btn" variant="outline" className={`rounded-sm ${isSuper ? "" : "hidden"}`}>
              <Download className="h-4 w-4 mr-2" /> Export Excel
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm flex-wrap h-auto">
            <TabsTrigger value="pending_approval" data-testid="tab-pending-users">Pending</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active-users">Active</TabsTrigger>
            <TabsTrigger value="suspended" data-testid="tab-suspended-users">Suspended</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected-users">Rejected</TabsTrigger>
            <TabsTrigger value="deactivated" data-testid="tab-deactivated-users">Deactivated</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all-users">All</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-4 border border-[#E5E2DC] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-users-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Mobile</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Registered</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-[#5C6653]">Loading…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-[#5C6653]">No users.</td></tr>
              ) : (
                users.map((u) => {
                  const st = normalizeStatus(u.status);
                  const isPending = st === "pending_approval";
                  const isActive = st === "active";
                  const isSuspended = st === "suspended";
                  return (
                    <tr key={u.id} className="border-b border-[#E5E2DC]" data-testid={`user-row-${u.email}`}>
                      <Td>
                        <div className="font-medium">{u.name}</div>
                        {u.company_name && <div className="text-xs text-[#5C6653]">{u.company_name}</div>}
                        {isSuspended && u.suspension_reason && (
                          <div className="text-[11px] text-red-700 mt-1">Reason: {u.suspension_reason}</div>
                        )}
                      </Td>
                      <Td><span className="text-xs">{u.email}</span></Td>
                      <Td><span className="text-xs">{u.mobile}</span></Td>
                      <Td><span className="text-xs uppercase tracking-wider text-[#5C6653]">{roleLabel(u.role)}</span></Td>
                      <Td><StatusPill status={st} /></Td>
                      <Td><span className="text-xs text-[#5C6653]">{formatDate(u.created_at)}</span></Td>
                      <Td>
                        <div className="flex gap-1.5 flex-wrap">
                          {isPending && (
                            <>
                              <Button size="sm" onClick={() => approve(u.id, "agent")} data-testid={`approve-agent-${u.email}`} className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white h-8">
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Channel Partner
                              </Button>
                              {isSuper && (
                                <Button size="sm" variant="outline" onClick={() => approve(u.id, "admin")} data-testid={`approve-admin-${u.email}`} className="rounded-sm h-8">
                                  <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Admin
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => reject(u.id)} data-testid={`reject-${u.email}`} className="rounded-sm h-8 border-red-300 text-red-700 hover:bg-red-50">
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                            </>
                          )}

                          {isActive && canSuspend(u) && (
                            <Button size="sm" variant="outline" onClick={() => openSuspend(u)} data-testid={`suspend-${u.email}`}
                              className="rounded-sm h-8 border-orange-300 text-orange-700 hover:bg-orange-50">
                              <Ban className="h-3.5 w-3.5 mr-1" /> Suspend
                            </Button>
                          )}
                          {isSuspended && canSuspend(u) && (
                            <Button size="sm" onClick={() => openReactivate(u)} data-testid={`reactivate-${u.email}`}
                              className="rounded-sm h-8 bg-[#347355] hover:bg-[#26593F] text-white">
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivate
                            </Button>
                          )}

                          {isAdmin && !isPending && (
                            <button onClick={() => openHistory(u)} data-testid={`history-${u.email}`}
                              title="View history"
                              className="h-8 w-8 flex items-center justify-center border border-[#E5E2DC] hover:bg-[#F0EFEA] rounded-sm">
                              <History className="h-3.5 w-3.5" />
                            </button>
                          )}

                          {isSuper && !isPending && (
                            <>
                              <button onClick={() => openEdit(u)} data-testid={`edit-${u.email}`}
                                title="Edit"
                                className="h-8 w-8 flex items-center justify-center border border-[#E5E2DC] hover:bg-[#F0EFEA] rounded-sm">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              {u.id !== user.id && (
                                <button onClick={() => del(u)} data-testid={`delete-${u.email}`}
                                  title="Delete"
                                  className="h-8 w-8 flex items-center justify-center border border-red-300 text-red-700 hover:bg-red-50 rounded-sm">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create user */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add user</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3" data-testid="create-user-form">
            <FormRow label="Full Name" required>
              <Input required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="create-name" className="rounded-sm" />
            </FormRow>
            <FormRow label="Email" required>
              <Input required type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="create-email" className="rounded-sm" />
            </FormRow>
            <FormRow label="Mobile" required>
              <Input required value={createForm.mobile} onChange={(e) => setCreateForm({ ...createForm, mobile: e.target.value })} data-testid="create-mobile" className="rounded-sm" />
            </FormRow>
            <FormRow label="Password" required>
              <Input required type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} data-testid="create-password" className="rounded-sm" />
            </FormRow>
            <FormRow label="Company (optional)">
              <Input value={createForm.company_name} onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })} data-testid="create-company" className="rounded-sm" />
            </FormRow>
            <FormRow label="Role">
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger data-testid="create-role" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Channel Partner</SelectItem>
                  {isSuper && <SelectItem value="admin">Admin</SelectItem>}
                </SelectContent>
              </Select>
            </FormRow>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" data-testid="create-user-submit" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit user */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md rounded-sm">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Edit user · {editing.email}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3" data-testid="edit-user-form">
                <FormRow label="Name">
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" className="rounded-sm" />
                </FormRow>
                <FormRow label="Mobile">
                  <Input value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} data-testid="edit-user-mobile" className="rounded-sm" />
                </FormRow>
                <FormRow label="Company">
                  <Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} data-testid="edit-user-company" className="rounded-sm" />
                </FormRow>
                <FormRow label="Role">
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger data-testid="edit-user-role" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent">Channel Partner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </FormRow>
                <FormRow label="Status">
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger data-testid="edit-user-status" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending_approval">Pending Approval</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="deactivated">Deactivated</SelectItem>
                    </SelectContent>
                  </Select>
                </FormRow>
                <FormRow label="Reset Password (optional)">
                  <Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="edit-user-password" placeholder="Leave blank to keep" className="rounded-sm" />
                </FormRow>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={saveEdit} data-testid="save-edit-user" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend user */}
      <Dialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="suspend-dialog">
          {suspendTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <Ban className="h-5 w-5 text-orange-600" /> Suspend user
                </DialogTitle>
                <DialogDescription>
                  You are about to suspend <span className="font-medium text-[#1A1F16]">{suspendTarget.name}</span> ({suspendTarget.email}).
                  They will be immediately signed out and blocked from all portal actions.
                </DialogDescription>
              </DialogHeader>

              {suspendWarnings && suspendWarnings.active_bookings > 0 && (
                <div className="flex items-start gap-2 text-sm text-orange-800 bg-orange-50 border border-orange-200 px-3 py-2 rounded-sm" data-testid="suspend-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    This user has <span className="font-semibold">{suspendWarnings.active_bookings}</span> active/pending booking(s).
                    Their existing records will be preserved but no further activity will be possible.
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <FormRow label="Reason" required>
                  <Input value={suspendForm.reason} onChange={(e) => setSuspendForm({ ...suspendForm, reason: e.target.value })} placeholder="e.g. Policy violation" data-testid="suspend-reason" className="rounded-sm" />
                </FormRow>
                <FormRow label="Additional notes (optional)">
                  <Textarea rows={3} value={suspendForm.notes} onChange={(e) => setSuspendForm({ ...suspendForm, notes: e.target.value })} data-testid="suspend-notes" className="rounded-sm" />
                </FormRow>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSuspendTarget(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={confirmSuspend} data-testid="suspend-confirm" className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white">
                  <Ban className="h-4 w-4 mr-2" /> Confirm suspension
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reactivate user */}
      <Dialog open={!!reactivateTarget} onOpenChange={(o) => !o && setReactivateTarget(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="reactivate-dialog">
          {reactivateTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-[#347355]" /> Reactivate user
                </DialogTitle>
                <DialogDescription>
                  Restore access for <span className="font-medium text-[#1A1F16]">{reactivateTarget.name}</span> ({reactivateTarget.email}).
                </DialogDescription>
              </DialogHeader>
              {reactivateTarget.suspension_reason && (
                <div className="text-xs bg-[#F0EFEA] border border-[#E5E2DC] px-3 py-2 rounded-sm">
                  <div className="text-[#5C6653]">Suspended on {formatDate(reactivateTarget.suspended_at)} — Reason:</div>
                  <div className="text-[#1A1F16] mt-0.5">{reactivateTarget.suspension_reason}</div>
                </div>
              )}
              <FormRow label="Reactivation notes (optional)">
                <Textarea rows={3} value={reactivateNotes} onChange={(e) => setReactivateNotes(e.target.value)} data-testid="reactivate-notes" className="rounded-sm" />
              </FormRow>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReactivateTarget(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={confirmReactivate} data-testid="reactivate-confirm" className="rounded-sm bg-[#347355] hover:bg-[#26593F] text-white">
                  <RotateCcw className="h-4 w-4 mr-2" /> Reactivate
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* User history */}
      <Dialog open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="history-dialog">
          {historyTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <History className="h-5 w-5" /> User history · {historyTarget.email}
                </DialogTitle>
                <DialogDescription>Suspension, reactivation and administrative actions</DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto border border-[#E5E2DC] rounded-sm">
                {historyEntries.length === 0 ? (
                  <div className="text-center py-8 text-sm text-[#5C6653]">No history yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[#F0EFEA] sticky top-0">
                      <tr>
                        <Th>When</Th>
                        <Th>Action</Th>
                        <Th>By</Th>
                        <Th>Details</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyEntries.map((h) => (
                        <tr key={h.id} className="border-t border-[#E5E2DC]" data-testid={`history-row-${h.id}`}>
                          <Td><span className="text-xs">{formatDate(h.timestamp)}</span></Td>
                          <Td><span className="text-xs font-medium">{h.action}</span></Td>
                          <Td>
                            <div className="text-xs">{h.actor_name || h.actor_email || "—"}</div>
                            {h.actor_role && <div className="text-[10px] uppercase text-[#5C6653]">{h.actor_role}</div>}
                          </Td>
                          <Td>
                            <pre className="text-[11px] whitespace-pre-wrap font-mono-anu text-[#3B4436]">{h.details ? JSON.stringify(h.details, null, 2) : "—"}</pre>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function FormRow({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-medium mb-1 block">{label}{required && <span className="text-[#C06C47]"> *</span>}</Label>
      {children}
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    pending_approval: { bg: "#EAB30820", color: "#B08B00", label: "Pending" },
    active: { bg: "#34735520", color: "#347355", label: "Active" },
    suspended: { bg: "#EA580C20", color: "#C2410C", label: "Suspended" },
    rejected: { bg: "#DC262620", color: "#DC2626", label: "Rejected" },
    deactivated: { bg: "#5C665320", color: "#5C6653", label: "Deactivated" },
  };
  const s = map[status] || { bg: "#F0EFEA", color: "#5C6653", label: status };
  return <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: s.bg, borderColor: s.color, color: s.color }}>{s.label}</span>;
}
